import { ref, computed, watch } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { notify } from 'notiwind';
import { useSubscription } from '@/composables/useSubscription';

export function useBonusAlert(props) {
    const bonusAlertLoading = ref(false);
    const bonusAlertEnabled = ref(props.bonusAlertEnabled);
    const openBonusAlert = ref(null);

    const { isSubscribed } = useSubscription();

    const alertsFeatureEnabled = computed(() => !!props.features?.alerts_enabled);

    // Transfer bonus alerts are a 10xTravel+ feature: paid users manage alerts,
    // unpaid users see the upsell banner instead of the toggle UI.
    const canManageBonusAlerts = computed(
        () => alertsFeatureEnabled.value && isSubscribed.value
    );
    const showBonusAlertUpsell = computed(
        () => alertsFeatureEnabled.value && !isSubscribed.value
    );

    watch(() => props.bonusAlertEnabled, (newValue) => {
        if (!bonusAlertLoading.value) {
            bonusAlertEnabled.value = newValue;
        }
    });

    const handleFlashMessages = () => {
        const page = usePage();
        if (page.props.flash?.bonusAlert) {
            const flash = page.props.flash.bonusAlert;
            notify({
                group: flash.type,
                title: flash.type === 'success'
                    ? (flash.enabled ? 'Bonus alert enabled' : 'Bonus alert disabled')
                    : 'Error',
                text: flash.message,
            }, 4000);
        }
    };

    watch(() => usePage().props.flash?.bonusAlert, (newFlash) => {
        if (newFlash && !bonusAlertLoading.value) handleFlashMessages();
    });

    const toggleBonusAlert = (enabled) => {
        if (!canManageBonusAlerts.value || bonusAlertLoading.value) return;

        const previousValue = bonusAlertEnabled.value;
        bonusAlertLoading.value = true;
        bonusAlertEnabled.value = enabled;

        router.post(`/bonus-alerts/${props.program.id}/toggle`, { enabled }, {
            preserveState: true,
            preserveScroll: true,
            onSuccess: () => {
                notify({
                    group: 'success',
                    title: enabled ? 'Bonus alert enabled' : 'Bonus alert disabled',
                    text: `Bonus alert ${enabled ? 'enabled' : 'disabled'} for ${props.program.name}.`,
                }, 4000);
            },
            onFinish: () => {
                bonusAlertLoading.value = false;
                openBonusAlert.value = null;
            },
            onError: (errors) => {
                bonusAlertEnabled.value = previousValue;
                notify({
                    group: 'error',
                    title: 'Error',
                    text: errors.error || 'An error occurred while updating bonus alert preferences.',
                }, 4000);
            },
        });
    };

    return {
        bonusAlertLoading,
        bonusAlertEnabled,
        openBonusAlert,
        alertsFeatureEnabled,
        canManageBonusAlerts,
        showBonusAlertUpsell,
        toggleBonusAlert,
        handleFlashMessages,
    };
}
